import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { logger } from '../common/logger';
import { CRUNCHBASE_ORGANIZATIONS, CrunchbaseOrganization } from '../graphql/queries/crunchbase';

interface CBDataState {
    isLoading: boolean;
    hasFetched: boolean;
    crunchbaseInfo?: CrunchbaseOrganization;
}

interface CBDataContextType {
    getCompanyData: (url: string) => CBDataState;
    requestCompanyData: (url: string) => void;
}

const CBDataContext = createContext<CBDataContextType | undefined>(undefined);

const debouncePeriodMs = 200;

interface CrunchbaseDataProviderProps {
    children?: React.ReactNode;
}

export const CrunchbaseDataProvider = ({ children }: CrunchbaseDataProviderProps): JSX.Element => {
    const [data, setData] = useState<{ [url: string]: CBDataState }>({});
    const [urlsToFetch, setUrlsToFetch] = useState<Set<string>>(new Set());
    const [fetchingUrls, setFetchingUrls] = useState<Set<string>>(new Set());
    const [fetchData] = useLazyQuery<{ organizations: CrunchbaseOrganization[] }, { linkedinUrls: string[] }>(
        CRUNCHBASE_ORGANIZATIONS
    );

    const debouncedFetchData = useMemo(
        () =>
            debounce(async (urls: Set<string>) => {
                const urlArray = Array.from(urls);
                try {
                    setUrlsToFetch((prev) => new Set([...Array.from(prev)].filter((url) => !urls.has(url))));
                    setFetchingUrls((prev) => new Set([...Array.from(prev), ...urlArray]));
                    const res = await fetchData({ variables: { linkedinUrls: urlArray } });
                    if (res.error) {
                        throw res.error;
                    }
                    setData((prev) => {
                        let updated = { ...prev };
                        for (const url of urlArray) {
                            const orgData = res.data.organizations.find((org) => org.recruiter_url?.url === url);
                            updated = {
                                ...updated,
                                [url]: { isLoading: false, hasFetched: true, crunchbaseInfo: orgData }
                            };
                        }
                        return updated;
                    });
                    setFetchingUrls((prev) => new Set([...Array.from(prev)].filter((url) => !urlArray.includes(url))));
                } catch (err) {
                    logger.info('Error fetching crunchbase data', { error: err.message, urls: urlArray });
                    setUrlsToFetch((prev) => new Set([...Array.from(prev), ...urlArray]));
                    setFetchingUrls((prev) => new Set([...Array.from(prev)].filter((url) => !urlArray.includes(url))));
                }
            }, debouncePeriodMs),
        [fetchData]
    );

    useEffect(() => {
        if (urlsToFetch.size > 0) {
            debouncedFetchData(urlsToFetch);
        }
    }, [urlsToFetch, debouncedFetchData]);

    const getCBData = useCallback(
        (url: string): CBDataState => {
            if (!url) {
                return { isLoading: false, hasFetched: false };
            }
            if (data[url]) {
                return data[url];
            }
            const isLoading = fetchingUrls.has(url) || urlsToFetch.has(url);
            return { isLoading, hasFetched: false };
        },
        [data, fetchingUrls, urlsToFetch]
    );

    const requestCompanyData = useCallback(
        (url: string) => {
            if (!url || data[url] || fetchingUrls.has(url)) {
                return;
            }
            setUrlsToFetch((prev) => {
                if (prev.has(url)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(url);
                return next;
            });
        },
        [data, fetchingUrls]
    );

    const value = useMemo(
        () => ({
            getCompanyData: getCBData,
            requestCompanyData
        }),
        [getCBData, requestCompanyData]
    );

    return <CBDataContext.Provider value={value}>{children}</CBDataContext.Provider>;
};

export const useCrunchbaseData = (url: string): CBDataState => {
    const context = useContext(CBDataContext);
    if (!context) {
        throw new Error('useCrunchbaseData must be used within a CrunchbaseDataProvider');
    }

    const { getCompanyData, requestCompanyData } = context;
    const state = getCompanyData(url);
    const shouldRequest = Boolean(url && !state.hasFetched && !state.isLoading);

    useEffect(() => {
        if (shouldRequest) {
            requestCompanyData(url);
        }
    }, [shouldRequest, requestCompanyData, url]);

    if (shouldRequest) {
        return { ...state, isLoading: true };
    }

    return state;
};
